import { SetStateAction, useEffect, useRef, useState } from "react";

interface TrustmaryWidgetProps {
  setShowTrustmaryWidget: React.Dispatch<SetStateAction<boolean>>;
}

const TRUSTMARY_SCRIPT_SRC = "https://widget.trustmary.com/nGyxIvWXU";
const MAX_WAIT_MS = 2000;
const CHECK_INTERVAL_MS = 200;

export default function TrustmaryWidget({
  setShowTrustmaryWidget,
}: TrustmaryWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);

  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;

    const hideWidget = () => {
      if (cancelled) return;

      setHasError(true);
      setShowTrustmaryWidget(false);
      clearPendingTimeout();
    };

    const clearPendingTimeout = () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const findTrustmaryWidget = () =>
      container.querySelector<HTMLElement>("[data-trustmary-status]");

    const handleWidgetReady = (widgetElement: HTMLElement) => {
      setScriptLoaded(true);

      if (containsMonthlyReset(widgetElement)) {
        hideWidget();
      }
    };

    const waitForWidget = (waited = 0) => {
      if (cancelled) return;

      const widgetElement = findTrustmaryWidget();
      const widgetStatus = widgetElement?.getAttribute("data-trustmary-status");

      if (widgetElement && widgetStatus) {
        handleWidgetReady(widgetElement);
        return;
      }

      if (waited >= MAX_WAIT_MS) {
        hideWidget();
        return;
      }

      timeoutRef.current = window.setTimeout(() => {
        waitForWidget(waited + CHECK_INTERVAL_MS);
      }, CHECK_INTERVAL_MS);
    };

    const existingWidget = findTrustmaryWidget();

    if (existingWidget) {
      handleWidgetReady(existingWidget);
      return;
    }

    const existingScript = container.querySelector<HTMLScriptElement>(
      `script[src="${TRUSTMARY_SCRIPT_SRC}"]`
    );

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = TRUSTMARY_SCRIPT_SRC;
      script.async = true;

      script.onload = () => {
        if (!cancelled) waitForWidget();
      };

      script.onerror = () => {
        hideWidget();
      };

      container.appendChild(script);
    } else {
      waitForWidget();
    }

    return () => {
      cancelled = true;
      clearPendingTimeout();

      container
        .querySelectorAll("[data-trustmary-status]")
        .forEach((widget) => widget.remove());
    };
  }, [setShowTrustmaryWidget]);

  if (hasError) return null;

  return (
    <div
      ref={containerRef}
      className="d-flex justify-content-center align-items-center"
    >
      {!scriptLoaded && (
        <div className="d-flex justify-content-center align-items-center">
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          />
          Loading review widget...
        </div>
      )}
    </div>
  );
}

function containsMonthlyReset(parentElement: HTMLElement): boolean {
  return Array.from(parentElement.querySelectorAll("*")).some((element) =>
    element.textContent?.toLowerCase().includes("monthly reset")
  );
}